import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Edit3, Copy, Receipt, DollarSign as DollarIcon, FileDown, ChevronDown, FilePlus2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";

const INV_STATUS = ["draft","sent","partial","paid","overdue","void"];
const STATUS_STYLE = {
  draft: "secondary", sent: "default", partial: "secondary",
  paid: "outline", overdue: "destructive", void: "outline",
};

export default function Finance() {
  const { settings } = useSettings() || {};
  const currency = settings?.currency || "AUD";
  const taxRate = settings?.tax_rate ?? 10;
  const taxLabel = settings?.tax_label || "GST";

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Finance</h1>
        <p className="text-sm text-muted-foreground mt-1">Invoices, payments, expenses — {taxLabel} defaults to {taxRate}% and {currency}.</p>
      </div>
      <Tabs defaultValue="invoices">
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices"><Invoices currency={currency} taxRate={taxRate} taxLabel={taxLabel} /></TabsContent>
        <TabsContent value="templates"><Templates currency={currency} taxRate={taxRate}/></TabsContent>
        <TabsContent value="expenses"><Expenses currency={currency} /></TabsContent>
        <TabsContent value="payments"><Payments currency={currency} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Invoices({ currency, taxRate, taxLabel }) {
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    const [i, c] = await Promise.all([api.get("/invoices"), api.get("/companies")]);
    setList(i.data); setCompanies(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/invoices/${editing.id}`, form);
      else await api.post("/invoices", form);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const clone = async (id) => { await api.post(`/invoices/${id}/clone`); toast.success("Cloned"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete invoice?")) return; await api.delete(`/invoices/${id}`); load(); };
  const markSent = async (inv) => { await api.put(`/invoices/${inv.id}`, { status: "sent" }); load(); };
  const downloadPdf = async (inv, layout = "modern") => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { params: { layout }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number || "invoice"}-${layout}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const recordPayment = async (form) => {
    try { await api.post("/payments", { ...form, invoice_id: payFor.id }); toast.success("Payment recorded"); setPayOpen(false); load(); }
    catch { toast.error("Failed"); }
  };

  const outstanding = list.reduce((s, i) => s + (i.status !== "paid" && i.status !== "void" && i.status !== "draft" ? (i.total - (i.amount_paid || 0)) : 0), 0);
  const drafts = list.filter((i) => i.status === "draft").length;

  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/invoice-templates").then(({ data }) => setTemplates(data)); }, []);
  const applyTemplate = async (tId) => {
    try { await api.post(`/invoices/from-template/${tId}`); toast.success("Invoice drafted from template"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 flex-1">
          <StatChip label="Outstanding" value={money(outstanding, currency)} />
          <StatChip label="Invoices" value={list.length} />
          <StatChip label="Drafts" value={drafts} />
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild><Button data-testid="add-invoice-btn" onClick={() => setEditing({})}><Plus className="w-4 h-4 mr-2"/> New invoice</Button></DialogTrigger>
          <InvoiceDialog invoice={editing} onSave={save} companies={companies} taxRate={taxRate} taxLabel={taxLabel} currency={currency} />
        </Dialog>
        {templates.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="use-template-btn"><FilePlus2 className="w-4 h-4 mr-2"/> Use template</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">Templates</DropdownMenuLabel>
              {templates.map((t) => (
                <DropdownMenuItem key={t.id} data-testid={`use-template-${t.id}`} onClick={() => applyTemplate(t.id)}>{t.name}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Card className="studio-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No invoices yet.</TableCell></TableRow>
            ) : list.map((i) => (
              <TableRow key={i.id} data-testid={`invoice-row-${i.id}`}>
                <TableCell className="font-medium">{i.number}</TableCell>
                <TableCell>{i.client_name || "—"}</TableCell>
                <TableCell>{i.issue_date}</TableCell>
                <TableCell>{i.due_date}</TableCell>
                <TableCell>{money(i.total, i.currency || currency)}</TableCell>
                <TableCell>{money(i.amount_paid || 0, i.currency || currency)}</TableCell>
                <TableCell><Badge variant={STATUS_STYLE[i.status] || "secondary"} className="capitalize text-[10px]">{i.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {i.status === "draft" && <Button variant="ghost" size="sm" data-testid={`send-invoice-${i.id}`} onClick={() => markSent(i)}>Mark sent</Button>}
                  {i.status !== "paid" && <Button variant="ghost" size="sm" data-testid={`pay-invoice-${i.id}`} onClick={() => { setPayFor(i); setPayOpen(true); }}><DollarIcon className="w-3.5 h-3.5"/></Button>}
                  <Button variant="ghost" size="sm" data-testid={`pdf-invoice-${i.id}`} onClick={() => downloadPdf(i, "modern")} title="Download PDF (modern)"><FileDown className="w-3.5 h-3.5"/></Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid={`pdf-layout-${i.id}`} title="Choose PDF layout" className="-ml-2 px-1"><ChevronDown className="w-3.5 h-3.5"/></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">PDF layout</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => downloadPdf(i, "modern")}>Modern (colour band)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadPdf(i, "minimal")}>Minimal (editorial)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadPdf(i, "classic")}>Classic (serif)</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" data-testid={`clone-invoice-${i.id}`} onClick={() => clone(i.id)}><Copy className="w-3.5 h-3.5"/></Button>
                  <Button variant="ghost" size="sm" data-testid={`edit-invoice-${i.id}`} onClick={() => { setEditing(i); setOpen(true); }}><Edit3 className="w-3.5 h-3.5"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(i.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <PaymentDialog invoice={payFor} onSave={recordPayment} currency={currency}/>
      </Dialog>
    </div>
  );
}

function InvoiceDialog({ invoice, onSave, companies, taxRate, taxLabel, currency }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF({
      number: invoice?.number || "",
      client_id: invoice?.client_id || "",
      client_name: invoice?.client_name || "",
      client_type: invoice?.client_type || "company",
      issue_date: invoice?.issue_date || "",
      due_date: invoice?.due_date || "",
      currency: invoice?.currency || currency,
      status: invoice?.status || "draft",
      notes: invoice?.notes || "",
      reminders_paused: invoice?.reminders_paused || false,
      lines: invoice?.lines?.length ? invoice.lines : [{ description: "", quantity: 1, unit_price: 0, tax_rate: taxRate }],
    });
  }, [invoice, currency, taxRate]);

  const subtotal = (f.lines || []).reduce((s, l) => s + (l.quantity * l.unit_price), 0);
  const taxTotal = (f.lines || []).reduce((s, l) => s + (l.quantity * l.unit_price * (l.tax_rate/100)), 0);
  const total = subtotal + taxTotal;

  const updateLine = (idx, patch) => setF({ ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) });
  const addLine = () => setF({ ...f, lines: [...(f.lines||[]), { description: "", quantity: 1, unit_price: 0, tax_rate: taxRate }] });
  const removeLine = (idx) => setF({ ...f, lines: f.lines.filter((_, i) => i !== idx) });

  const client = companies.find((c) => c.id === f.client_id);
  const numberHint = client?.invoice_prefix
    ? `next in ${client.invoice_prefix.replace(/-$/, "")} series → ${client.invoice_prefix.replace(/-$/, "")}-${String(client.next_invoice_number || 1).padStart(4, "0")}`
    : "auto-assigned from your workspace series";


  return (
    <DialogContent className="max-w-3xl">
      <DialogHeader><DialogTitle>{invoice?.id ? `Edit ${invoice.number}` : "New invoice"}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Invoice number</Label>
            <Input
              data-testid="invoice-number"
              value={f.number || ""}
              placeholder={numberHint}
              onChange={(e) => setF({...f, number: e.target.value})}
            />
            <p className="text-[11px] text-muted-foreground">{invoice?.id ? "Edit to override" : `Leave blank → ${numberHint}`}</p>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Client</Label>
            <Select value={f.client_id || "__none__"} onValueChange={(v) => {
              if (v === "__none__") return setF({ ...f, client_id: "", client_name: "" });
              const c = companies.find((x) => x.id === v);
              setF({ ...f, client_id: v, client_name: c?.name || "" });
            }}>
              <SelectTrigger data-testid="invoice-client"><SelectValue placeholder="Select client"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (manual name)</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Client name (override)</Label>
            <Input data-testid="invoice-client-name" value={f.client_name || ""} onChange={(e) => setF({...f, client_name: e.target.value})}/>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({...f, status: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{INV_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Issue date</Label><Input type="date" value={f.issue_date || ""} onChange={(e) => setF({...f, issue_date: e.target.value})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">Due date</Label><Input type="date" value={f.due_date || ""} onChange={(e) => setF({...f, due_date: e.target.value})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">Currency</Label><Input value={f.currency || ""} onChange={(e) => setF({...f, currency: e.target.value})}/></div>
        </div>

        <div className="border border-border rounded-lg p-3">
          <div className="text-xs font-medium mb-2 uppercase tracking-wider text-muted-foreground">Line items</div>
          {(f.lines || []).map((l, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center">
              <Input className="col-span-5" placeholder="Description" value={l.description} onChange={(e) => updateLine(idx, { description: e.target.value })} />
              <Input className="col-span-2" type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(idx, { quantity: parseFloat(e.target.value) || 0 })} />
              <Input className="col-span-2" type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(idx, { unit_price: parseFloat(e.target.value) || 0 })} />
              <Input className="col-span-2" type="number" step="0.01" value={l.tax_rate} onChange={(e) => updateLine(idx, { tax_rate: parseFloat(e.target.value) || 0 })} />
              <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeLine(idx)}><Trash2 className="w-3.5 h-3.5"/></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn"><Plus className="w-3.5 h-3.5 mr-1"/> Add line</Button>
          <div className="mt-3 flex justify-end gap-6 text-sm">
            <div>Subtotal: <span className="font-medium">{money(subtotal, f.currency)}</span></div>
            <div>{taxLabel}: <span className="font-medium">{money(taxTotal, f.currency)}</span></div>
            <div>Total: <span className="font-semibold">{money(total, f.currency)}</span></div>
          </div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({...f, notes: e.target.value})}/></div>

        <div className="flex items-center gap-2">
          <Switch checked={!!f.reminders_paused} data-testid="invoice-pause-reminders"
            onCheckedChange={(v) => setF({...f, reminders_paused: v})}/>
          <span className="text-xs text-muted-foreground">Pause automatic reminders for this invoice</span>
        </div>
        {(invoice?.reminder_log || []).length > 0 && (
          <div className="rounded-lg border border-border p-3 text-[11px] space-y-1" data-testid="invoice-reminder-log">
            <div className="uppercase tracking-wider text-muted-foreground">Reminders sent</div>
            {invoice.reminder_log.slice(-4).reverse().map((r, i) => (
              <div key={i} className="text-muted-foreground">
                {new Date(r.at).toLocaleString()} · {r.status} · {r.to || "no email"}{r.detail ? ` · ${r.detail}` : ""}
              </div>
            ))}
          </div>
        )}
      </div>
      <DialogFooter><Button data-testid="invoice-save" onClick={() => onSave(f)}>Save invoice</Button></DialogFooter>
    </DialogContent>
  );
}

function PaymentDialog({ invoice, onSave, currency }) {
  const [f, setF] = useState({ amount: "", method: "bank_transfer", paid_on: new Date().toISOString().slice(0,10), notes: "" });
  useEffect(() => { if (invoice) setF({ ...f, amount: (invoice.total - (invoice.amount_paid || 0)).toFixed(2) }); /* eslint-disable-next-line */ }, [invoice]);
  if (!invoice) return null;
  return (
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Record payment on {invoice.number}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground">Balance: {money((invoice.total || 0) - (invoice.amount_paid || 0), invoice.currency || currency)}</div>
        <div className="space-y-1.5"><Label className="text-xs">Amount</Label><Input data-testid="payment-amount" type="number" step="0.01" value={f.amount} onChange={(e) => setF({...f, amount: e.target.value})}/></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Method</Label>
            <Select value={f.method} onValueChange={(v) => setF({...f, method: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={f.paid_on} onChange={(e) => setF({...f, paid_on: e.target.value})}/></div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})}/></div>
      </div>
      <DialogFooter><Button data-testid="payment-save" onClick={() => onSave({ ...f, amount: parseFloat(f.amount) || 0 })}>Record</Button></DialogFooter>
    </DialogContent>
  );
}

function Expenses({ currency }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await api.get("/expenses"); setList(data); };
  useEffect(() => { load(); }, []);
  const save = async (f) => {
    try {
      if (editing?.id) await api.put(`/expenses/${editing.id}`, f); else await api.post("/expenses", f);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete expense?")) return; await api.delete(`/expenses/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild><Button data-testid="add-expense-btn" onClick={() => setEditing({})}><Plus className="w-4 h-4 mr-2"/> Add expense</Button></DialogTrigger>
          <ExpenseDialog expense={editing} onSave={save} currency={currency}/>
        </Dialog>
      </div>
      <Card className="studio-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Category</TableHead><TableHead>Amount</TableHead><TableHead>Billable</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No expenses recorded.</TableCell></TableRow>
             : list.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{e.date}</TableCell>
                <TableCell>{e.description}</TableCell>
                <TableCell><Badge variant="secondary" className="text-[10px]">{e.category}</Badge></TableCell>
                <TableCell>{money(e.amount, e.currency)}</TableCell>
                <TableCell>{e.is_billable ? "Yes" : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(e); setOpen(true); }}><Edit3 className="w-3.5 h-3.5"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(e.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function ExpenseDialog({ expense, onSave, currency }) {
  const [f, setF] = useState({});
  useEffect(() => { setF({
    description: expense?.description || "",
    category: expense?.category || "general",
    amount: expense?.amount || 0,
    currency: expense?.currency || currency,
    date: expense?.date || new Date().toISOString().slice(0,10),
    is_billable: expense?.is_billable || false,
    notes: expense?.notes || "",
  }); }, [expense, currency]);
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>{expense?.id ? "Edit expense" : "Add expense"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5"><Label className="text-xs">Description</Label><Input data-testid="expense-description" value={f.description || ""} onChange={(e) => setF({...f, description: e.target.value})}/></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Amount</Label><Input data-testid="expense-amount" type="number" step="0.01" value={f.amount || 0} onChange={(e) => setF({...f, amount: parseFloat(e.target.value) || 0})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">Currency</Label><Input value={f.currency || ""} onChange={(e) => setF({...f, currency: e.target.value})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">Category</Label><Input value={f.category || ""} onChange={(e) => setF({...f, category: e.target.value})}/></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" value={f.date || ""} onChange={(e) => setF({...f, date: e.target.value})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">Billable</Label>
            <Select value={f.is_billable ? "yes" : "no"} onValueChange={(v) => setF({...f, is_billable: v === "yes"})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <DialogFooter><Button data-testid="expense-save" onClick={() => onSave(f)}>Save</Button></DialogFooter>
    </DialogContent>
  );
}

function Payments({ currency }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/payments").then(({ data }) => setList(data)); }, []);
  return (
    <Card className="studio-card overflow-hidden">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>Invoice</TableHead><TableHead>Amount</TableHead><TableHead>Method</TableHead><TableHead>Notes</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {list.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No payments recorded yet.</TableCell></TableRow>
           : list.map((p) => (
            <TableRow key={p.id}>
              <TableCell>{p.paid_on}</TableCell>
              <TableCell className="font-mono text-xs">{p.invoice_id.slice(0,8)}</TableCell>
              <TableCell>{money(p.amount, currency)}</TableCell>
              <TableCell><Badge variant="secondary" className="text-[10px]">{p.method}</Badge></TableCell>
              <TableCell className="text-xs text-muted-foreground">{p.notes || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

const StatChip = ({ label, value }) => (
  <div className="studio-card px-4 py-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-lg font-semibold mt-0.5">{value}</div>
  </div>
);

function Templates({ currency, taxRate }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/invoice-templates"); setList(data); };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/invoice-templates/${editing.id}`, form);
      else await api.post("/invoice-templates", form);
      toast.success("Template saved"); setOpen(false); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete template?")) return; await api.delete(`/invoice-templates/${id}`); load(); };
  const applyTemplate = async (t) => { try { await api.post(`/invoices/from-template/${t.id}`); toast.success("Draft invoice created"); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild><Button data-testid="add-template-btn" onClick={() => setEditing({})}><Plus className="w-4 h-4 mr-2"/> New template</Button></DialogTrigger>
          <TemplateDialog template={editing} onSave={save} currency={currency} taxRate={taxRate}/>
        </Dialog>
      </div>
      {list.length === 0 ? (
        <Card className="studio-card p-10 text-center">
          <FilePlus2 className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5}/>
          <div className="text-lg font-medium">No templates yet</div>
          <p className="text-sm text-muted-foreground mt-1">Save reusable line items so retainers and repeat clients take one click.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((t) => {
            const subtotal = (t.lines || []).reduce((s, l) => s + (l.quantity * l.unit_price), 0);
            return (
              <Card key={t.id} className="studio-card p-5" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.default_client_name || "No default client"} · Due in {t.due_days} days · {t.pdf_layout}</div>
                    {t.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{t.description}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-muted-foreground">Subtotal</div>
                    <div className="font-semibold">{money(subtotal, t.currency || currency)}</div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-border flex justify-between items-center">
                  <div className="text-xs text-muted-foreground">{(t.lines || []).length} lines</div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => applyTemplate(t)} data-testid={`template-use-${t.id}`}>Use</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setOpen(true); }}><Edit3 className="w-3.5 h-3.5"/></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(t.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TemplateDialog({ template, onSave, currency, taxRate }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF({
      name: template?.name || "",
      description: template?.description || "",
      default_client_name: template?.default_client_name || "",
      currency: template?.currency || currency,
      pdf_layout: template?.pdf_layout || "modern",
      due_days: template?.due_days ?? 14,
      notes: template?.notes || "",
      lines: template?.lines?.length ? template.lines : [{ description: "", quantity: 1, unit_price: 0, tax_rate: taxRate }],
    });
  }, [template, currency, taxRate]);

  const updateLine = (idx, patch) => setF({ ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) });
  const addLine = () => setF({ ...f, lines: [...(f.lines||[]), { description: "", quantity: 1, unit_price: 0, tax_rate: taxRate }] });
  const removeLine = (idx) => setF({ ...f, lines: f.lines.filter((_, i) => i !== idx) });

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>{template?.id ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input data-testid="template-name" value={f.name || ""} onChange={(e) => setF({...f, name: e.target.value})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">Default client name</Label><Input value={f.default_client_name || ""} onChange={(e) => setF({...f, default_client_name: e.target.value})}/></div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea rows={2} value={f.description || ""} onChange={(e) => setF({...f, description: e.target.value})}/></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Currency</Label><Input value={f.currency || ""} onChange={(e) => setF({...f, currency: e.target.value})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">Due in (days)</Label><Input type="number" value={f.due_days ?? 14} onChange={(e) => setF({...f, due_days: parseInt(e.target.value) || 14})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">PDF layout</Label>
            <select className="border border-border rounded-md h-10 px-2 text-sm bg-background" value={f.pdf_layout} onChange={(e) => setF({...f, pdf_layout: e.target.value})}>
              <option value="modern">Modern</option>
              <option value="minimal">Minimal</option>
              <option value="classic">Classic</option>
            </select>
          </div>
        </div>
        <div className="border border-border rounded-lg p-3">
          <div className="text-xs font-medium mb-2 uppercase tracking-wider text-muted-foreground">Line items</div>
          {(f.lines || []).map((l, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center">
              <Input className="col-span-5" placeholder="Description" value={l.description} onChange={(e) => updateLine(idx, { description: e.target.value })} />
              <Input className="col-span-2" type="number" step="0.01" value={l.quantity} onChange={(e) => updateLine(idx, { quantity: parseFloat(e.target.value) || 0 })} />
              <Input className="col-span-2" type="number" step="0.01" value={l.unit_price} onChange={(e) => updateLine(idx, { unit_price: parseFloat(e.target.value) || 0 })} />
              <Input className="col-span-2" type="number" step="0.01" value={l.tax_rate} onChange={(e) => updateLine(idx, { tax_rate: parseFloat(e.target.value) || 0 })} />
              <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeLine(idx)}><Trash2 className="w-3.5 h-3.5"/></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addLine}><Plus className="w-3.5 h-3.5 mr-1"/> Add line</Button>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Default notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({...f, notes: e.target.value})}/></div>
      </div>
      <DialogFooter><Button data-testid="template-save" onClick={() => onSave(f)}>Save template</Button></DialogFooter>
    </DialogContent>
  );
}

